import { Component, HostListener, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { IdleService } from '../../../core/services/idle.service';

/**
 * "Still there?" idle-timeout warning — drop this once in the top-level
 * layout (app.html), same convention as <app-toast-container>. Subscribes to
 * IdleService.warning$ so it works on every route (admin + public) without
 * any per-feature wiring.
 *
 * Accessibility (WCAG 2.2 — legal requirement on this platform, per
 * CLAUDE.md): role="alertdialog" (not a plain "dialog" — this interrupts the
 * user with a time-sensitive choice), a focus trap (cdkTrapFocus, the same
 * mechanism already used by pdf-fill-modal / confirm-delete-dialog / the
 * shared app-modal in this app — not hand-rolled), an aria-live region so
 * the countdown is announced to screen readers as it ticks down, and two
 * independent keyboard-dismiss paths: Escape, and a "Stay signed in" button
 * that is the first focusable element (cdkTrapFocusAutoCapture).
 */
@Component({
  selector: 'app-idle-warning-modal',
  standalone: false,
  templateUrl: './idle-warning-modal.component.html',
  styleUrls: ['./idle-warning-modal.component.scss'],
})
export class IdleWarningModalComponent implements OnDestroy {
  visible = false;
  secondsLeft = 0;
  private destroy$ = new Subject<void>();

  constructor(private idleService: IdleService) {
    this.idleService.warning$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.visible = state.visible;
      this.secondsLeft = state.secondsLeft;
    });
  }

  /** "Stay signed in" button + Escape key — both dismiss the warning and
   *  reset the idle clock via IdleService (single source of truth for what
   *  "still there" means; this component holds no timing logic of its own). */
  stayActive(): void {
    this.idleService.stayActive();
  }

  @HostListener('document:keydown.escape')
  onEscKey(): void {
    if (this.visible) this.stayActive();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
